import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { ENV } from '../utils/env.js';

/**
 * Custom hook for polling auto-fix job status and updating findings in real-time
 * 
 * @param {Function} showToast - Toast notification function
 * @param {Function} updateFindingStatus - Function to update finding status in the UI
 * @param {object} options - Configuration options
 * @returns {object} Hook interface with polling functions
 */
export const useAutoFixPolling = (showToast, updateFindingStatus, options = {}) => {
  const {
    pollInterval = 5000, // Poll every 5 seconds
    maxPollTime = 600000, // Stop polling after 10 minutes
    enableLogging = false
  } = options;

  // Track active polling jobs
  const [activeJobs, setActiveJobs] = useState(new Map());
  const pollIntervals = useRef(new Map());
  const pollTimeouts = useRef(new Map());

  const log = useCallback((message, data = null) => {
    if (enableLogging) {
      console.log(`[AutoFixPolling] ${message}`, data || '');
    }
  }, [enableLogging]);

  /**
   * Start polling for a specific auto-fix job
   * @param {string} jobId - The auto-fix job ID to poll
   * @param {string} findingId - The finding ID associated with this job
   * @param {string} integrationType - Integration type (for endpoint resolution)
   */
  const startPolling = useCallback((jobId, findingId, integrationType = 'github') => {
    if (!jobId || !findingId) {
      log('Cannot start polling: missing jobId or findingId', { jobId, findingId });
      return;
    }

    // Stop any existing polling for this job
    stopPolling(jobId);

    log(`Starting polling for job ${jobId} (finding: ${findingId})`);

    // Add to active jobs
    setActiveJobs(prev => new Map(prev.set(jobId, {
      findingId,
      integrationType,
      startTime: Date.now(),
      status: 'processing'
    })));

    // Set up polling interval
    const intervalId = setInterval(async () => {
      try {
        await pollJobStatus(jobId, findingId, integrationType);
      } catch (error) {
        log(`Error polling job ${jobId}:`, error);
        // Continue polling on errors, but log them
      }
    }, pollInterval);

    // Set up timeout to stop polling after maxPollTime
    const timeoutId = setTimeout(() => {
      log(`Polling timeout reached for job ${jobId}`);
      handleJobCompletion(jobId, findingId, {
        status: 'timeout',
        error: 'Auto-fix job timed out. Please check the status manually.'
      });
    }, maxPollTime);

    // Store interval and timeout IDs
    pollIntervals.current.set(jobId, intervalId);
    pollTimeouts.current.set(jobId, timeoutId);

  }, [pollInterval, maxPollTime, updateFindingStatus, showToast, log]);

  /**
   * Poll the status of a specific job
   */
  const pollJobStatus = useCallback(async (jobId, findingId, integrationType) => {
    try {
      log(`Polling status for job ${jobId}`);
      
      // Use the AI_URL for auto-fix status endpoints
      const baseUrl = ENV.AI_URL || ENV.API_URL;
      const response = await axios.get(
        `${baseUrl}api/v1/remediation/jobs/${jobId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const jobStatus = response.data;
      log(`Job ${jobId} status:`, jobStatus);

      // Check if job is complete
      if (jobStatus.status === 'completed' || jobStatus.status === 'success') {
        handleJobCompletion(jobId, findingId, {
          status: 'fixed',
          pull_request_url: jobStatus.pull_request_url,
          message: 'Auto-fix completed successfully!'
        });
      } else if (jobStatus.status === 'failed' || jobStatus.status === 'error') {
        handleJobCompletion(jobId, findingId, {
          status: 'failed',
          error: jobStatus.error_message || 'Auto-fix failed'
        });
      } else if (jobStatus.status === 'skipped') {
        handleJobCompletion(jobId, findingId, {
          status: 'skipped',
          message: 'Auto-fix was skipped for this finding'
        });
      }
      // If status is still 'processing' or 'pending', continue polling

    } catch (error) {
      log(`Error polling job ${jobId}:`, error);
      
      // If we get a 404, the job might be complete or doesn't exist
      if (error.response?.status === 404) {
        handleJobCompletion(jobId, findingId, {
          status: 'not_found',
          error: 'Auto-fix job not found. It may have completed or expired.'
        });
      }
      // For other errors, continue polling (might be temporary network issues)
    }
  }, [log, showToast, updateFindingStatus]);

  /**
   * Handle job completion and cleanup
   */
  const handleJobCompletion = useCallback((jobId, findingId, result) => {
    log(`Job ${jobId} completed with status: ${result.status}`);

    // Stop polling for this job
    stopPolling(jobId);

    // Update finding status based on result
    if (result.status === 'fixed') {
      updateFindingStatus(findingId, {
        status: 'fixed',
        pull_request_url: result.pull_request_url
      });
      if (result.message) {
        showToast(result.message, 'success');
      }
    } else if (result.status === 'failed' || result.status === 'timeout' || result.status === 'not_found') {
      // Reset to normal status (remove processing state)
      updateFindingStatus(findingId, {
        status: null
      });
      if (result.error) {
        showToast(result.error, 'error');
      }
    } else if (result.status === 'skipped') {
      // Reset to normal status
      updateFindingStatus(findingId, {
        status: null
      });
      if (result.message) {
        showToast(result.message, 'info');
      }
    }

    // Remove from active jobs
    setActiveJobs(prev => {
      const newMap = new Map(prev);
      newMap.delete(jobId);
      return newMap;
    });

  }, [log, updateFindingStatus, showToast]);

  /**
   * Stop polling for a specific job
   */
  const stopPolling = useCallback((jobId) => {
    log(`Stopping polling for job ${jobId}`);

    // Clear interval
    const intervalId = pollIntervals.current.get(jobId);
    if (intervalId) {
      clearInterval(intervalId);
      pollIntervals.current.delete(jobId);
    }

    // Clear timeout
    const timeoutId = pollTimeouts.current.get(jobId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pollTimeouts.current.delete(timeoutId);
    }
  }, [log]);

  /**
   * Stop all active polling
   */
  const stopAllPolling = useCallback(() => {
    log('Stopping all polling');
    
    // Clear all intervals
    pollIntervals.current.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    pollIntervals.current.clear();

    // Clear all timeouts
    pollTimeouts.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    pollTimeouts.current.clear();

    // Clear active jobs
    setActiveJobs(new Map());
  }, [log]);

  /**
   * Get status of active polling jobs
   */
  const getActiveJobs = useCallback(() => {
    return Array.from(activeJobs.entries()).map(([jobId, job]) => ({
      jobId,
      ...job,
      elapsedTime: Date.now() - job.startTime
    }));
  }, [activeJobs]);

  /**
   * Check if a specific job is being polled
   */
  const isPolling = useCallback((jobId) => {
    return activeJobs.has(jobId);
  }, [activeJobs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllPolling();
    };
  }, [stopAllPolling]);

  return {
    startPolling,
    stopPolling,
    stopAllPolling,
    getActiveJobs,
    isPolling,
    activeJobsCount: activeJobs.size
  };
};

export default useAutoFixPolling;